import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-200 text-foreground-400">
            <i className="ri-file-unknow-line text-2xl"></i>
          </div>
          <h2 className="mt-4 text-lg font-heading font-semibold text-foreground-900">文章未找到</h2>
          <Link to="/articles" className="mt-4 inline-block text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
            返回文章列表
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '亚洲精品',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>

      {/* Header Image */}
      <section className="relative w-full h-[300px] md:h-[450px] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/50"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 max-w-4xl mx-auto">
          <span className="inline-block px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded-full whitespace-nowrap mb-3">
            {article.category}
          </span>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-white leading-tight">{article.title}</h1>
        </div>
      </section>

      {/* Content */}
      <article className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <Breadcrumb items={[
          { label: '首页', to: '/' },
          { label: '精选文章', to: '/articles' },
          { label: article.title },
        ]} />

        <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-foreground-500">
          <span>{article.author}</span>
          <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
          <span>{article.date}</span>
          <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
          <span>{article.readTime} 分钟阅读</span>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          {article.tags.map((tag) => (
            <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-800 text-xs rounded-full whitespace-nowrap">
              {tag}
            </span>
          ))}
        </div>

        <div
          className="mt-8 prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
            [&_h2]:font-heading [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4
            [&_p]:my-4 [&_p]:leading-relaxed"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <div className="mt-12 pt-8 border-t border-background-200">
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> 返回文章列表
          </Link>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-6">相关文章</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.map((ra) => (
                <ArticleCard key={ra.id} article={ra} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}